import { isNumber } from '@art-suite/art-core-ts';
import { getEffectiveScanValue, ScanLineItemOptimized, ScanValue } from '@kaiizen/kai-scan-template-system';

import { presentUsd } from '@/Client/Presenters';

import { FocusedLineItemStrip, FocusedLineItemStripButton, FocusedLineItemStripMessage } from './FocusedLineItemStrip';
import { FocusedLineItemTab } from './FocusedLineItemTab';

const isUsdWithinTolerance = (left: number, right: number, tolerance: number = 0.001) => Math.abs(left - right) < tolerance;

/**
 * Returns true when the line item's effective USD price is missing or differs from the catalog price.
 */
const catalogUsdPriceNeedsSyncToLineItem = (catalogPrice: number, priceValue: ScanValue | undefined): boolean => {
  const effectivePrice = getEffectiveScanValue(priceValue);
  if (effectivePrice == null) return true;
  if (typeof effectivePrice !== 'number') return true;
  return !isUsdWithinTolerance(effectivePrice, catalogPrice);
};

export const shouldShowApproveCatalogPriceChange = (params: {
  scanIsEditable: boolean;
  defaultPrice: number | undefined;
  lineItemId: string | undefined;
  optionId: string | undefined;
  lineItemPrice: ScanValue | undefined;
}): boolean => {
  const { scanIsEditable, defaultPrice, lineItemId, optionId, lineItemPrice } = params;
  if (!scanIsEditable) return false;
  if (!lineItemPrice) return false;
  if (!isNumber(defaultPrice)) return false;
  if (!lineItemId || !optionId) return false;
  if (!catalogUsdPriceNeedsSyncToLineItem(defaultPrice, lineItemPrice)) return false;
  return true;
};

/**
 * The line item's total at a new parts price. Material cost is linear in the price (price × quantity,
 * or × an overage quantity), so it scales by the ratio of the prices; labor is unaffected.
 */
export const getLineItemTotalAtPartsPrice = (lineItem: ScanLineItemOptimized, partsUsd: number): number => {
  const { materialCost, laborCost } = lineItem.optimized?.reducedValues ?? {};
  const currentPartsUsd = getEffectiveScanValue(lineItem.values.price);
  const quantity = getEffectiveScanValue(lineItem.values.quantity);
  const materialCostAtPartsPrice = isNumber(currentPartsUsd) && currentPartsUsd !== 0 && isNumber(materialCost)
    ? materialCost * partsUsd / currentPartsUsd
    : partsUsd * (isNumber(quantity) ? quantity : 1);
  return materialCostAtPartsPrice + (isNumber(laborCost) ? laborCost : 0);
};

/**
 * The strip under a row whose catalog price has moved: its tag, the move and what it does to the total,
 * with Accept to take the catalog price. Always visible, so the change can't go unnoticed.
 */
export const FocusedApproveCatalogPriceChange = ({
  lineItemPrice,
  catalogUsd,
  totalAtCatalogPrice,
  onApprove,
}: {
  lineItemPrice: ScanValue | undefined;
  catalogUsd: number;
  totalAtCatalogPrice: number;
  onApprove: () => void;
}) => {
  const effectivePrice = getEffectiveScanValue(lineItemPrice);
  return (
    <FocusedLineItemStrip
      testId='focused-line-item-price-change-strip'
      actions={<FocusedLineItemStripButton onClick={onApprove}>Accept</FocusedLineItemStripButton>}
    >
      <FocusedLineItemTab
        testId='focused-line-item-price-change-tab'
        explanationTitle='Product SKU Price Change'
        explanation="The product's catalog price has changed since this line was priced."
        hint='Accept to take the new price.'
      >
        {'Price change'}
      </FocusedLineItemTab>
      <FocusedLineItemStripMessage>
        {isNumber(effectivePrice)
          ? (
            <>
              Unit price {catalogUsd > effectivePrice ? 'rose' : 'fell'} <s>{presentUsd(effectivePrice)}</s> → <b>{presentUsd(catalogUsd)}</b>.
            </>
          )
          : (
            <>
              Unit price is <b>{presentUsd(catalogUsd)}</b>.
            </>
          )} Total goes to <b>{presentUsd(totalAtCatalogPrice)}</b>.
      </FocusedLineItemStripMessage>
    </FocusedLineItemStrip>
  );
};
